import { Injectable, InjectionToken, Provider } from '@angular/core';
import { PaginatedResponse } from '../../shared/pagination/paginated-response';
import { ApiLegalFoundation } from '../api-types/legal-foundation.api';
import { LegalFoundation } from '../app-types/legal-foundation.app';
import { transformApiLegalFoundationsToAppLegalFoundations } from '../transformers/legal-foundation.transform';
import { BaseRequestService } from './base-request.service';

export const LEGAL_FOUNDATION_SERVICE = new InjectionToken<LegalFoundationService>('LEGAL_FOUNDATION_SERVICE');

export type LegalFoundationQuery = Record<string, unknown> & {
	serviceOfficeStatementOnly?: boolean;
	range?: number;
};

export interface LegalFoundationApi {
	getAll(offset?: number, range?: number): Promise<PaginatedResponse<LegalFoundation>>;
	getAllForTypeOfCareAndSponsor(
		typeOfCareId: number | string,
		sponsorId: number | string,
		offset?: number,
		range?: number,
	): Promise<PaginatedResponse<LegalFoundation>>;
	getValidTemplatesAmount(legalFoundationId: number | string): Promise<number>;
}

/**
 * Service for getting legal foundations for the API.
 */
@Injectable()
export class LegalFoundationService extends BaseRequestService implements LegalFoundationApi {
	public async getAll(offset?: number, range?: number) {
		const response = await this.get<PaginatedResponse<ApiLegalFoundation>>('legalFoundations', { offset, range });
		const legalFoundations = transformApiLegalFoundationsToAppLegalFoundations(response.entities);
		const page: PaginatedResponse<LegalFoundation> = {
			entities: legalFoundations,
			offset: response.offset,
			range: response.range,
			totalEntities: response.totalEntities,
		};
		return page;
	}

	public async getAllForTypeOfCareAndSponsor(
		typeOfCareId: number | string,
		sponsorId: number | string,
		offset?: number,
		range?: number,
	) {
		const response = await this.get<PaginatedResponse<ApiLegalFoundation>>(
			`legalFoundations/sponsors/${sponsorId}/typeOfCares/${typeOfCareId}`,
			{ offset, range },
		);
		const legalFoundations = transformApiLegalFoundationsToAppLegalFoundations(response.entities);
		const page: PaginatedResponse<LegalFoundation> = {
			entities: legalFoundations,
			offset: response.offset,
			range: response.range,
			totalEntities: response.totalEntities,
		};
		return page;
	}

	public async getValidTemplatesAmount(legalFoundationId: number | string) {
		return this.get<number>(`legalFoundations/${legalFoundationId}/validTemplates/amount`);
	}
}

@Injectable()
export class LegalFoundationMockService extends BaseRequestService implements LegalFoundationApi {
	public constructor() {
		super();
		console.warn('⛔️⛔️⛔️ Legal Foundation Mock Service: You are currently using a mock service!');
	}

	private readonly legalFoundations: LegalFoundation[] = [
		{
			id: 1,
			sponsorId: 1,
			integrationOfficeId: 1,
			description: 'LVR Budget für Arbeit',
		},
		{
			id: 2,
			sponsorId: 2,
			integrationOfficeId: 1,
			description: 'Sicherung der VM (§ 102 Abs. 2 i.V.m. § 113)',
		},
		{
			id: 3,
			sponsorId: 2,
			integrationOfficeId: 1,
			description: 'Vorleistung (§ 102 Abs. 6 SGB IX)',
		},
	];

	public getAllForTypeOfCareAndSponsor = async (
		_typeOfCareId: number | string,
		sponsorId: number | string,
		offset?: number,
		range?: number,
	) => {
		const filtered = this.legalFoundations.filter((x) => x.sponsorId === Number(sponsorId));
		const page: PaginatedResponse<LegalFoundation> = {
			entities: filtered,
			offset: offset ?? 0,
			range: range ?? 100,
			totalEntities: filtered.length,
		};
		return page;
	};

	public getAll = async (offset?: number, range?: number) => {
		const page: PaginatedResponse<LegalFoundation> = {
			entities: this.legalFoundations,
			offset: offset ?? 0,
			range: range ?? 100,
			totalEntities: this.legalFoundations.length,
		};
		return page;
	};

	public getValidTemplatesAmount = async (_legalFoundationId: number | string) => {
		return 1;
	};
}

export const provideLegalFoundationApi: Provider = {
	provide: LEGAL_FOUNDATION_SERVICE,
	useClass: LegalFoundationService,
};
